import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  Button,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import UserProfileCard from './UserProfileCard';
import FollowService from '../../services/follow-service';
import { FollowUserProfile, UserList } from '../../types/follow';
import { logger } from '../../utils/logger';
import { useDebounce } from '../../hooks/useDebounce';

interface FollowingListProps {
  userId: string;
  title?: string;
  showSearch?: boolean;
  maxHeight?: number | string;
  onUserClick?: (userId: string) => void;
}

/**
 * Component for displaying and searching through users that a user is following
 */
const FollowingList: React.FC<FollowingListProps> = ({
  userId,
  title = 'Following',
  showSearch = true,
  maxHeight = 600,
  onUserClick,
}) => {
  const [following, setFollowing] = useState<FollowUserProfile[]>([]);
  const [filteredFollowing, setFilteredFollowing] = useState<FollowUserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0,
    total: 0,
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Load following
    const loadFollowing = useCallback(async (reset: boolean = false) => {
    if (!userId) {
      setError('No user ID provided');
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    try {
      if (reset) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMore(true);
      }

      const offset = reset ? 0 : pagination.offset;
      const result = await FollowService.getFollowing(userId, pagination.limit, offset);
      
      // Ensure result has proper structure with fallbacks
      if (!result) {
        throw new Error('No response from API');
      }

      const users = result.users || [];
      const paginationData = result.pagination || {};
      
      if (reset) {
        setFollowing(users);
      } else {
        setFollowing(prev => [...prev, ...users]);
      }

      setPagination({
        limit: paginationData.limit || pagination.limit,
        offset: paginationData.nextOffset || paginationData.offset + paginationData.limit || offset + pagination.limit,
        total: paginationData.total || 0,
      });

      setHasMore(paginationData.hasMore || false);
      
      logger.info('Loaded following', { 
        userId, 
        count: users.length, 
        total: paginationData.total || 0,
        reset 
      });
    } catch (error: any) {
      logger.error('Failed to load following:', error);
      setError(error.message || 'Failed to load following');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [userId, pagination.limit, pagination.offset]);

  // Initial load
  useEffect(() => {
    if (userId) {
      loadFollowing(true);
    } else {
      setError('No user ID provided');
      setIsLoading(false);
    }
  }, [userId, loadFollowing]);

  // Filter following based on search query
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setFilteredFollowing(following);
      return;
    }

    const query = debouncedSearchQuery.toLowerCase();
    const filtered = following.filter(user => 
      user.displayName.toLowerCase().includes(query) ||
      user.username.toLowerCase().includes(query)
    );

    setFilteredFollowing(filtered);
  }, [following, debouncedSearchQuery]);

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      loadFollowing(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadFollowing(true);
  };

  // Render loading skeletons
  const renderLoadingSkeletons = (count: number = 3) => (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <UserProfileCard
          key={`skeleton-${index}`}
          user={{} as FollowUserProfile}
          isLoading={true}
          showFollowButton={false}
        />
      ))}
    </>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" component="h2">
          {title} ({(pagination.total || 0).toLocaleString()})
        </Typography>
        
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Search */}
      {showSearch && (
        <TextField
          fullWidth
          placeholder="Search following..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
          size="small"
        />
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Following List */}
      <Box
        sx={{
          maxHeight,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        {/* Loading initial data */}
        {isLoading && following.length === 0 && renderLoadingSkeletons(5)}

        {/* No following */}
        {!isLoading && following.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              Not following anyone yet
            </Typography>
          </Box>
        )}

        {/* No search results */}
        {!isLoading && following.length > 0 && filteredFollowing.length === 0 && searchQuery && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No users found matching "{searchQuery}"
            </Typography>
          </Box>
        )}

        {/* Following */}
        {filteredFollowing.map((user, index) => (
          <Box key={user.id}>
            <UserProfileCard
              user={user}
              showFollowButton={true}
              showStats={true}
              size="medium"
              onClick={onUserClick}
            />
            {index < filteredFollowing.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        ))}

        {/* Load More Button */}
        {!searchQuery && hasMore && filteredFollowing.length > 0 && (
          <Box textAlign="center" mt={2}>
            <Button
              onClick={handleLoadMore}
              disabled={isLoadingMore}
              startIcon={isLoadingMore ? <CircularProgress size={16} /> : undefined}
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}

        {/* Loading more indicator */}
        {isLoadingMore && (
          <Box mt={2}>
            {renderLoadingSkeletons(2)}
          </Box>
        )}

        {/* End of list indicator */}
        {!hasMore && filteredFollowing.length > 0 && !searchQuery && (
          <Box textAlign="center" py={2}>
            <Typography variant="caption" color="text.secondary">
              You've reached the end of the list
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FollowingList;